'use client'

import { C, Callout, Exercise, Figure, H, Hl, Lesson, P, Quiz, Step } from '../_components/lesson'
import { VariableBoxes } from '../_components/widgets/CodeVisuals'
import * as EX from './exercises/code-basics'

export default function ValuesAndVariables() {
  return (
    <Lesson>
      <Step>
        <H>Everything is a value</H>
        <P>
          A program is a list of instructions that push <strong>values</strong> around. For almost everything in this course you
          only need three kinds:
        </P>
        <ul className="gd-list">
          <li>
            <Hl c="blue">numbers</Hl> — <C>42</C>, <C>-3</C>, <C>0.001</C>. Whole or decimal, it’s all one type.
          </li>
          <li>
            <Hl c="green">strings</Hl> — text, always inside quotes: <C>&quot;hello&quot;</C>.
          </li>
          <li>
            <Hl c="violet">booleans</Hl> — just <C>true</C> or <C>false</C>. Yes or no.
          </li>
        </ul>
        <Exercise id="value-kinds" title="Three kinds of value" {...EX.valueKinds} />
      </Step>

      <Step>
        <H>Variables are labelled boxes</H>
        <P>
          A <strong>variable</strong> is a name with a value inside — a labelled box. You make one with <C>const</C> (the box is
          sealed: its value never changes) or <C>let</C> (you can swap what’s inside later).
        </P>
        <Figure n="1.1" caption="Three variables. The label is the name; the box holds the value; the type says what kind of value is allowed inside.">
          <VariableBoxes
            items={[
              { name: 'name', value: '"Ada"', type: 'string' },
              { name: 'score', value: '15', type: 'number', kind: 'let' },
              { name: 'isReady', value: 'true', type: 'boolean' },
            ]}
          />
        </Figure>
        <Exercise id="boxes" title="Filling and refilling boxes" {...EX.boxes} />
        <Callout kind="plain">
          <C>score = score + 5</C> isn’t a math equation (that would be impossible!). Read it right-to-left:{' '}
          <em>work out score + 5, then put the result back in the score box.</em>
        </Callout>
      </Step>

      <Step>
        <H>Types: promises about what’s in the box</H>
        <P>
          The <em>Type</em> in TypeScript means you can label each box with what kind of value it’s allowed to hold — and the
          editor will stop you from putting the wrong thing in. Write the type after a colon: <C>let age: number = 36</C>.
        </P>
        <Exercise id="type-check" title="TypeScript catches a mistake" {...EX.typeCheck} />
        <P>
          That red underline appeared <em>before</em> you ran anything. In bigger programs — like the neural networks you’ll
          build — this saves hours: mixing up a list of numbers with a single number is one of the most common bugs in machine
          learning code, and TypeScript catches it as you type.
        </P>
        <Callout kind="note">
          Often you don’t need to write the type at all — <C>const x = 5</C> already tells TypeScript it’s a number. Write
          types where they make your intention clearer.
        </Callout>
      </Step>

      <Step gate="receipt">
        <H>Building values from other values</H>
        <P>
          You can compute new values from existing ones, and put values inside text with a <strong>template string</strong> —
          backticks instead of quotes, with <C>{'${…}'}</C> marking where a value goes: <C>{'`I am ${age} years old`'}</C>.
        </P>
        <Exercise
          id="receipt"
          title="A tiny receipt"
          {...EX.receipt}
          hints={[
            <>
              Multiplication uses <C>*</C>: <C>price * quantity</C>.
            </>,
            <>
              Template strings use backticks: <C>{'`Total: ${total}`'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['q-logic', 'logic']}>
        <H>True or false</H>
        <P>Comparisons produce booleans, and booleans combine with three operators:</P>
        <div className="gd-card my-6 overflow-x-auto p-1">
          <table className="gd-table">
            <thead>
              <tr>
                <th>Write</th>
                <th>Means</th>
                <th>Example → result</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['a === b', 'a equals b', '3 === 3 → true'],
                ['a !== b', 'a does not equal b', '3 !== 4 → true'],
                ['a > b, a >= b', 'greater (or equal)', '5 >= 5 → true'],
                ['p && q', 'p AND q (both true)', 'true && false → false'],
                ['p || q', 'p OR q (at least one)', 'true || false → true'],
                ['!p', 'NOT p (flip it)', '!true → false'],
              ].map(r => (
                <tr key={r[0]}>
                  <td>
                    <C>{r[0]}</C>
                  </td>
                  <td>{r[1]}</td>
                  <td className="gd-mono text-[13px]">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout kind="warn">
          One <C>=</C> <em>puts</em> a value in a box. Three <C>===</C> <em>asks</em> whether two values are equal. Mixing them
          up is a classic bug.
        </Callout>
        <Quiz
          id="q-logic"
          question={
            <>
              What is <C>5 &gt; 3 &amp;&amp; 2 &gt; 4</C>?
            </>
          }
          options={[
            { label: <C>true</C>, why: '5 > 3 is true, but 2 > 4 is false — and && needs both sides to be true.' },
            { label: <C>false</C>, correct: true },
            { label: 'an error', why: 'It’s perfectly valid: two comparisons combined with AND.' },
          ]}
          explain="&& is only true when both sides are. 2 > 4 is false, so the whole thing is false."
        />
        <Exercise id="logic" title="Who gets in?" {...EX.logic} hints={[<>Use <C>&gt;=</C> for “at least” and <C>&amp;&amp;</C> for “and”.</>, <>For skipQueue: <C>isVip || age &gt; 65</C>.</>]} />
      </Step>

      <Step>
        <H>That’s the foundation</H>
        <P>
          Values, named boxes, types, and true/false logic. Every machine-learning model in this course — including a small GPT
          — is built from exactly these pieces, plus the three ideas in the next lessons: <strong>functions</strong>,{' '}
          <strong>lists</strong> and <strong>loops</strong>.
        </P>
      </Step>
    </Lesson>
  )
}
